import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Truck } from "lucide-react";

// Stylized route: Chandler (top) -> Gilbert -> Tempe -> Sun Lakes -> Maricopa (bottom)
const ROUTE = "M 300 40 C 340 90, 330 120, 300 150 C 260 190, 180 180, 150 230 C 120 280, 170 320, 140 370 C 120 405, 90 420, 70 440";
const VIEW_W = 380;

const PINS = [
  { city: "Chandler", x: 300, y: 40 },
  { city: "Gilbert", x: 300, y: 150 },
  { city: "Tempe", x: 150, y: 230 },
  { city: "Sun Lakes", x: 140, y: 370 },
  { city: "Maricopa", x: 70, y: 440 },
];

const LOOP_MS = 12000;

export const CoverageMap = () => {
  const pathRef = useRef(null);
  const vanRef = useRef(null);
  const wrapRef = useRef(null);

  // Drive the van with getPointAtLength on the real SVG path — resolution
  // independent and reliable in every browser (CSS offset-path on SVG <g> is not).
  useEffect(() => {
    const path = pathRef.current;
    const van = vanRef.current;
    const wrap = wrapRef.current;
    if (!path || !van || !wrap) return;
    const total = path.getTotalLength();
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = ((now - start) % LOOP_MS) / LOOP_MS;
      const pt = path.getPointAtLength(total * t);
      const scale = wrap.getBoundingClientRect().width / VIEW_W;
      const fadeIn = Math.min(1, t / 0.06);
      const fadeOut = Math.min(1, (1 - t) / 0.08);
      van.style.opacity = String(Math.min(fadeIn, fadeOut));
      van.style.transform = `translate(${pt.x * scale}px, ${pt.y * scale}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div data-testid="coverage-map" className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b from-brand-navy to-brand-navy-light p-4 shadow-xl shadow-brand-blue/10">
      <div ref={wrapRef} className="relative">
        <svg viewBox="0 0 380 480" className="h-auto w-full" role="img" aria-label="Delivery route from Chandler to Maricopa">
          {/* route track */}
          <path d={ROUTE} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" strokeLinecap="round" />
          {/* animated dashes flowing along the route */}
          <motion.path
            d={ROUTE}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="10 14"
            animate={{ strokeDashoffset: [0, -240] }}
            transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
          />
          {/* invisible reference path used to position the van */}
          <path ref={pathRef} d={ROUTE} fill="none" stroke="none" />
          {PINS.map((p, i) => (
            <motion.g
              key={p.city}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 + i * 0.35, type: "spring", stiffness: 260, damping: 14 }}
            >
              <circle cx={p.x} cy={p.y} r={p.city === "Maricopa" ? 12 : 8} fill={p.city === "Maricopa" ? "#F59E0B" : "#2563EB"} stroke="#fff" strokeWidth="2.5" />
              <text
                x={p.x + 18}
                y={p.y + 5}
                fill="#fff"
                fontSize="15"
                fontWeight="700"
                fontFamily="Public Sans, sans-serif"
              >
                {p.city}
              </text>
            </motion.g>
          ))}
          {/* pulsing badge ring on Maricopa */}
          <motion.circle
            cx={70}
            cy={440}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2"
            initial={{ r: 12, opacity: 0.9 }}
            animate={{ r: [12, 34], opacity: [0.9, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeOut" }}
          />
        </svg>

        {/* van driving the route — HTML overlay positioned from the real path */}
        <div
          ref={vanRef}
          data-testid="coverage-map-van"
          aria-hidden
          className="absolute left-0 top-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-lg"
        >
          <Truck className="h-5 w-5 text-brand-blue" />
        </div>
      </div>

      <div className="absolute left-4 top-4 rounded-full bg-white/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-amber-200 backdrop-blur">
        Mon–Sat routes
      </div>
      <div
        data-testid="maricopa-pulse-badge"
        className="absolute bottom-4 left-4 right-4 flex items-center gap-2 rounded-2xl bg-fall-amber px-4 py-2.5 text-brand-navy shadow-lg"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-navy opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-navy" />
        </span>
        <p className="text-xs font-extrabold leading-tight">
          Now serving Maricopa — no dry cleaner in town, we come to you.
        </p>
      </div>
    </div>
  );
};
